export const QUICK_DEPOSIT_OPTIONS = Object.freeze([
  Object.freeze({ id: 'mehrweg-glas', field: 'glass_count', label: 'Mehrweg-Glas', hint: 'meist Glasflaschen', icon: '🍾', cents: 8, value: 0.08 }),
  Object.freeze({ id: 'mehrweg-pet', field: 'pet_reusable_count', label: 'Mehrweg-PET', hint: 'oft PET-Flaschen', icon: '🧴', cents: 15, value: 0.15 }),
  Object.freeze({ id: 'einweg', field: 'one_way_count', label: 'Einweg', hint: 'Dosen und Einweg-PET', icon: '🥫', cents: 25, value: 0.25 }),
]);

export const MAX_QUICK_ENTRY_AMOUNT = 25;
export const MAX_QUICK_ENTRY_ITEMS = 99;

export function normalizeCount(value) {
  const count = Number.parseInt(value, 10);
  if (!Number.isFinite(count)) return 0;
  return Math.max(0, Math.min(MAX_QUICK_ENTRY_ITEMS, count));
}
export function quickEntrySummary(counts = {}) {
  const lines = QUICK_DEPOSIT_OPTIONS.map((option) => ({ ...option, count: normalizeCount(counts[option.id]) }))
    .filter((item) => item.count > 0);
  const pieces = lines.reduce((sum, item) => sum + item.count, 0);
  const totalCents = lines.reduce((sum, item) => sum + item.cents * item.count, 0);
  const amount = totalCents / 100;
  return { lines, pieces, totalCents, amount, valid: pieces > 0 && pieces <= MAX_QUICK_ENTRY_ITEMS && totalCents > 0 && totalCents <= MAX_QUICK_ENTRY_AMOUNT * 100 };
}

export function createDonationMarkPayload(counts = {}) {
  const summary = quickEntrySummary(counts);
  if (!summary.valid) return null;
  return {
    ...Object.fromEntries(QUICK_DEPOSIT_OPTIONS.map((option) => [option.field, normalizeCount(counts[option.id])])),
    total_count: summary.pieces,
    total_cents: summary.totalCents,
  };
}

export function createQuickEntryPayload(counts = {}) {
  const summary = quickEntrySummary(counts);
  if (!summary.valid) return null;
  const breakdown = summary.lines.map((item) => `${item.count} × ${item.value.toFixed(2).replace('.', ',')} € ${item.label}`).join(', ');
  return {
    amount: summary.amount,
    meta: {
      activity_source: 'quick_add',
      quantity: summary.pieces,
      package_type: summary.pieces > 1 ? 'multipack' : 'single',
      package_label: `Schnelleingabe: ${breakdown}`,
      description: `Schnelleingabe: ${breakdown}`,
      unit_amount: Number((summary.amount / summary.pieces).toFixed(2)),
      package_deposit: summary.amount,
      awaitServer: true,
    },
  };
}

function localDateKey(value) {
  const date = value instanceof Date ? value : new Date(String(value || '').replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function inferEntryBreakdown(entry) {
  const text = `${entry?.package_label || ''} ${entry?.description || ''}`;
  const explicit = [...text.matchAll(/(\d+)\s*[×x]\s*(0[,.](?:08|15|25))/g)];
  if (explicit.length) return explicit.map((match) => ({ value: Number(match[2].replace(',', '.')), count: normalizeCount(match[1]) }));
  const pieces = Math.max(1, normalizeCount(entry?.quantity || 1) * Math.max(1, normalizeCount(entry?.package_units || 1)));
  const amount = Number(entry?.unit_amount ?? entry?.package_deposit ?? (Number(entry?.amount || 0) / pieces));
  return Number.isFinite(amount) ? [{ value: Number(amount.toFixed(2)), count: pieces }] : [];
}

export function aggregateDailyLoot(entries = [], now = new Date()) {
  const todayKey = localDateKey(now);
  const todayEntries = (Array.isArray(entries) ? entries : [])
    .filter((entry) => localDateKey(entry?.timestamp) === todayKey)
    .sort((left, right) => new Date(String(right.timestamp).replace(' ', 'T')) - new Date(String(left.timestamp).replace(' ', 'T')));
  const breakdownMap = new Map();
  let amount = 0;
  let pieces = 0;
  let xp = 0;
  todayEntries.forEach((entry) => {
    const entryAmount = Number(entry?.amount || 0);
    if (Number.isFinite(entryAmount)) amount += entryAmount;
    const parts = inferEntryBreakdown(entry);
    parts.forEach((part) => {
      pieces += part.count;
      const key = part.value.toFixed(2);
      breakdownMap.set(key, (breakdownMap.get(key) || 0) + part.count);
    });
    const entryXp = Number(entry?.xp_awarded ?? entry?.awarded ?? 0);
    if (Number.isFinite(entryXp)) xp += entryXp;
  });
  return {
    amount: Number(amount.toFixed(2)),
    pieces,
    xp,
    entries: todayEntries,
    breakdown: [...breakdownMap.entries()]
      .map(([value, count]) => ({ value: Number(value), count, subtotal: Number((Number(value) * count).toFixed(2)) }))
      .sort((left, right) => left.value - right.value),
  };
}
